import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  CircularProgress,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NotesIcon from '@mui/icons-material/Notes';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import {
  useBulkReviewBillingReconciliationFileHealth,
  useBillingReconciliationFileHealth,
  useBillingReconciliationFileHealthSummary,
  useRefreshBillingReconciliationFileHealth,
  useReviewBillingReconciliationFileHealth,
} from '../../hooks/api/useBillingReconciliationHooks.js';
import ReviewNoteDialog from './ReviewNoteDialog.jsx';
import SourceRoundBadge from './SourceRoundBadge.jsx';
import { billingErrorMessage } from './errorMessage.js';
import {
  fileHealthCodeLabel,
  fileHealthContext,
  fileHealthDealerName,
  fileHealthNotes,
  fileHealthOpenState,
  fileHealthSummaryText,
} from './fileHealthDisplay.js';

const EMPTY_FINDINGS = [];

const SEVERITY_PRESETS = [
  { value: 'error', label: 'Blocking', tier: 'error' },
  { value: 'warning', label: 'Warnings', tier: 'warning' },
  { value: 'all', label: 'All', tier: null },
];

const SEVERITY_META = {
  error: { label: 'Blocking', Icon: ErrorIcon, color: 'error.main' },
  warning: { label: 'Warnings', Icon: ReportProblemIcon, color: 'warning.main' },
  info: { label: 'Info', Icon: InfoOutlinedIcon, color: 'info.main' },
};

const normalizeReviewState = (state) => String(state || 'new').replace(/_/g, ' ');

const iconBoxSx = (color) => ({
  alignItems: 'center',
  color,
  display: 'inline-flex',
  justifyContent: 'center',
  lineHeight: 0,
});

const SeverityIcon = ({ severity }) => {
  const meta = SEVERITY_META[severity] || SEVERITY_META.info;
  const { Icon } = meta;
  return (
    <Tooltip title={meta.label}>
      <Box aria-label={`Severity: ${meta.label}`} role="img" sx={iconBoxSx(meta.color)}>
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

const MemoryIcon = ({ finding }) => {
  const hasMemory = Boolean(finding.reviewMemory?.reason || finding.reviewMemory);
  if (hasMemory) {
    return (
      <Tooltip title={finding.reviewMemory?.reason || 'Matched to prior reviewed alert memory'}>
        <Box aria-label="Matched prior reviewed alert memory" role="img" sx={iconBoxSx('success.main')}>
          <CheckCircleIcon fontSize="small" />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="No prior reviewed alert memory">
      <Box aria-label="No prior reviewed alert memory" role="img" sx={iconBoxSx('error.main')}>
        <CancelIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

const sortFileHealthRows = (rows) => [...rows].sort((left, right) => {
  const severityRank = { error: 0, warning: 1, info: 2 };
  return (severityRank[left.tier] ?? 3) - (severityRank[right.tier] ?? 3)
    || String(left.bac || '').localeCompare(String(right.bac || ''), undefined, { numeric: true })
    || String(left.code || '').localeCompare(String(right.code || ''))
    || Number(left.rowNumber || 0) - Number(right.rowNumber || 0);
});

const sortableValue = (finding, key) => {
  if (key === 'bac') return String(finding.bac || '');
  if (key === 'dealer') return fileHealthDealerName(finding);
  if (key === 'severity') return { error: 0, warning: 1, info: 2 }[finding.tier] ?? 3;
  if (key === 'memory') return finding.reviewMemory?.reason || finding.reviewMemory ? 1 : 0;
  if (key === 'status') return normalizeReviewState(finding.reviewState);
  if (key === 'source') return String(finding.fileKind || '');
  if (key === 'reviewed') return fileHealthOpenState(finding) ? 0 : 1;
  if (key === 'finding') return fileHealthCodeLabel(finding.code);
  if (key === 'detail') return fileHealthSummaryText(finding);
  return '';
};

const compareFileHealthRows = ({ sortBy, sortDirection }) => (left, right) => {
  const a = sortableValue(left, sortBy);
  const b = sortableValue(right, sortBy);
  const modifier = sortDirection === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number' && a !== b) return (a - b) * modifier;
  const compared = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  if (compared) return compared * modifier;
  return String(left.bac || '').localeCompare(String(right.bac || ''), undefined, { numeric: true });
};

const HeadCell = ({ id, label, align = 'left', sortBy, sortDirection, onSortChange }) => (
  <TableCell align={align}>
    <TableSortLabel
      active={sortBy === id}
      direction={sortBy === id ? sortDirection : 'asc'}
      onClick={() => onSortChange(id)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const FindingRow = ({ finding, onOpenBac, onReview, reviewPending }) => {
  const notes = fileHealthNotes(finding);
  const dealerName = fileHealthDealerName(finding);
  const summary = fileHealthSummaryText(finding);
  const isAddressed = !fileHealthOpenState(finding);
  const canOpen = Boolean(onOpenBac && finding.bac);
  const openBac = () => {
    if (canOpen) onOpenBac(finding.bac, fileHealthContext(finding));
  };
  const handleKeyDown = (event) => {
    if (!canOpen || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    openBac();
  };

  return (
    <TableRow
      hover
      tabIndex={canOpen ? 0 : undefined}
      aria-label={canOpen ? `Open BAC ${finding.bac} variance drawer` : undefined}
      onClick={openBac}
      onKeyDown={handleKeyDown}
      sx={{
        cursor: canOpen ? 'pointer' : 'default',
        opacity: isAddressed ? 0.72 : 1,
        '&:last-child td': { borderBottom: 0 },
        '&:hover': canOpen ? { outline: '1px solid', outlineColor: 'primary.main' } : undefined,
      }}
    >
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {finding.bac || '-'}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 260 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={dealerName || ''}>
          {dealerName || '-'}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={{ width: 58 }}>
        <SeverityIcon severity={finding.tier || 'info'} />
      </TableCell>
      <TableCell align="center" sx={{ width: 58 }}>
        <MemoryIcon finding={finding} />
      </TableCell>
      <TableCell sx={{ width: 120 }}>
        <Typography
          variant="caption"
          sx={{
            color: isAddressed ? 'success.main' : 'warning.main',
            fontWeight: 800,
            textTransform: 'capitalize',
          }}
        >
          {normalizeReviewState(finding.reviewState)}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={{ width: 68 }}>
        <SourceRoundBadge
          source={String(finding.fileKind || '').toLowerCase()}
          tooltipTitle={`${String(finding.fileKind || 'file').toUpperCase()} row ${finding.rowNumber || '-'}`}
        />
      </TableCell>
      <TableCell align="center" sx={{ width: 72 }}>
        <Tooltip title={isAddressed ? 'Reviewed' : 'Review with note'}>
          <span>
            <IconButton
              size="small"
              color={isAddressed ? 'success' : 'default'}
              disabled={isAddressed || reviewPending}
              aria-label={isAddressed ? 'Reviewed' : 'Review with note'}
              onClick={(event) => {
                event.stopPropagation();
                onReview?.(finding);
              }}
            >
              {isAddressed ? <CheckCircleIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: 190, maxWidth: 220 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={fileHealthCodeLabel(finding.code)}>
          {fileHealthCodeLabel(finding.code)}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 220, maxWidth: 320 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          {notes ? (
            <Tooltip title={`Source note: ${notes}`}>
              <NotesIcon color="info" fontSize="small" />
            </Tooltip>
          ) : null}
          <Tooltip title={summary}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {summary}
            </Typography>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const FileHealthPanel = ({ runId, limit = 500, onOpenBac = null }) => {
  const [showAddressed, setShowAddressed] = useState(false);
  const [severityPreset, setSeverityPreset] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('severity');
  const [sortDirection, setSortDirection] = useState('asc');
  const activePreset = SEVERITY_PRESETS.find((preset) => preset.value === severityPreset) || SEVERITY_PRESETS[2];
  const offset = (page - 1) * limit;
  const query = useBillingReconciliationFileHealth({ runId, limit, offset, tier: activePreset.tier, search, openOnly: !showAddressed });
  const summaryQuery = useBillingReconciliationFileHealthSummary({ runId });
  const refresh = useRefreshBillingReconciliationFileHealth();
  const review = useReviewBillingReconciliationFileHealth();
  const bulkReview = useBulkReviewBillingReconciliationFileHealth();
  const [reviewDialogFinding, setReviewDialogFinding] = useState(null);
  const [bulkReviewRequest, setBulkReviewRequest] = useState(null);
  const summary = summaryQuery.data || {};
  const findings = query.data || EMPTY_FINDINGS;
  const openFindings = useMemo(() => findings.filter(fileHealthOpenState), [findings]);
  const visibleRows = showAddressed ? findings : openFindings;
  const tableRows = useMemo(
    () => sortFileHealthRows(visibleRows).sort(compareFileHealthRows({ sortBy, sortDirection })),
    [sortBy, sortDirection, visibleRows],
  );
  const totalFindings = Number(findings.meta?.total ?? (showAddressed ? summary.total : summary.open) ?? findings.length);
  const openTotal = Number(summary.open ?? (!showAddressed ? totalFindings : openFindings.length));
  const visibleFindings = visibleRows.length;
  const pageCount = Math.max(1, Math.ceil(totalFindings / limit));
  const hasFindings = visibleRows.length > 0;
  const isInitialFindingsLoad = query.isLoading || query.isPending || (query.isFetching && !query.data);
  const reviewPending = review.isPending || bulkReview.isPending;
  const openCountByCode = useMemo(() => {
    const entries = new Map();
    for (const row of summary.byCode || []) {
      entries.set(String(row.code || '').toUpperCase(), Number(row.open || 0));
    }
    return entries;
  }, [summary.byCode]);

  useEffect(() => {
    setPage(1);
  }, [runId, severityPreset, search, showAddressed]);

  const submitReview = (reviewNote) => {
    const finding = reviewDialogFinding;
    if (!finding) return;
    review.mutate({
      id: finding.id,
      runId,
      findingFingerprint: finding.findingFingerprint,
      broadAlertKey: finding.broadAlertKey,
      materialFactsHash: finding.materialFactsHash,
      state: 'addressed',
      reviewNote,
    });
    setReviewDialogFinding(null);
  };
  const reviewFinding = (finding, reviewNote = null) => {
    if (reviewNote === null) {
      setReviewDialogFinding(finding);
      return;
    }
    review.mutate({
      id: finding.id,
      runId,
      findingFingerprint: finding.findingFingerprint,
      broadAlertKey: finding.broadAlertKey,
      materialFactsHash: finding.materialFactsHash,
      state: 'addressed',
      reviewNote,
    });
  };
  const reviewAll = () => {
    const ids = tableRows.filter(fileHealthOpenState).map((finding) => finding.id).filter(Boolean);
    if (!ids.length) return;
    setBulkReviewRequest({
      ids,
      title: `Review ${ids.length.toLocaleString()} file health finding${ids.length === 1 ? '' : 's'}?`,
      initialNote: 'Bulk reviewed from File Health.',
    });
  };
  const dismissByCode = (codes, reviewNote) => {
    const count = codes.reduce((sum, code) => sum + Number(openCountByCode.get(code) || 0), 0);
    if (!count) return;
    setBulkReviewRequest({
      codes,
      title: `Dismiss ${count.toLocaleString()} file health finding${count === 1 ? '' : 's'}?`,
      initialNote: reviewNote,
    });
  };
  const confirmBulkReview = (reviewNote) => {
    if (!bulkReviewRequest) return;
    bulkReview.mutate({
      runId,
      ids: bulkReviewRequest.ids,
      codes: bulkReviewRequest.codes,
      state: 'addressed',
      reviewNote: reviewNote?.trim() || bulkReviewRequest.initialNote || 'Bulk reviewed from File Health.',
    });
    setBulkReviewRequest(null);
  };
  const handleSortChange = (nextSortBy) => {
    if (sortBy === nextSortBy) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(nextSortBy);
    setSortDirection(['severity', 'memory', 'reviewed'].includes(nextSortBy) ? 'asc' : 'desc');
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FactCheckOutlinedIcon color={hasFindings ? 'warning' : 'success'} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              File Health
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Row-level parser and validation findings for the active round
            </Typography>
          </Box>
          <Chip
            size="small"
            color={openTotal ? 'warning' : 'success'}
            variant={openTotal ? 'filled' : 'outlined'}
            label={`${openTotal.toLocaleString()} active`}
            sx={{ fontWeight: 700 }}
          />
        </Stack>
        {refresh.isError ? (
          <Alert severity="error">
            File health alerts could not be recalculated from the retained source files.
          </Alert>
        ) : null}
        {refresh.isSuccess && refresh.data ? (
          <Alert severity="success">
            Recalculated file health: {Number(refresh.data.findingCount || 0).toLocaleString()} current findings.
          </Alert>
        ) : null}
        {review.isError ? (
          <Alert severity="error">{billingErrorMessage(review.error, 'File Health review state could not be saved.')}</Alert>
        ) : null}
        {bulkReview.isError ? (
          <Alert severity="error">{billingErrorMessage(bulkReview.error, 'File Health bulk review could not be saved.')}</Alert>
        ) : null}

        {isInitialFindingsLoad ? (
          <Stack spacing={1} aria-label="Loading file health findings" sx={{ minHeight: 116 }}>
            <Alert
              severity="info"
              icon={<CircularProgress color="inherit" size={16} />}
              sx={{ alignItems: 'center' }}
            >
              Loading file health findings...
            </Alert>
            <Skeleton height={34} />
            <Skeleton height={34} />
          </Stack>
        ) : query.isError ? (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => query.refetch()}>Retry</Button>}
          >
            File health could not be loaded.
          </Alert>
        ) : (
          <Collapse in collapsedSize={0}>
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <ButtonGroup size="small" variant="outlined" aria-label="File health severity preset">
                  {SEVERITY_PRESETS.map((preset) => (
                    <Button
                      key={preset.value}
                      variant={preset.value === severityPreset ? 'contained' : 'outlined'}
                      onClick={() => setSeverityPreset(preset.value)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </ButtonGroup>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Switch size="small" checked={showAddressed} onChange={(event) => setShowAddressed(event.target.checked)} inputProps={{ 'aria-label': 'Show addressed file health findings' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Show addressed</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: { sm: 'auto' }, minWidth: { xs: '100%', sm: 280 } }}>
                  <TextField
                    size="small"
                    label="Search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Tooltip title={refresh.isPending ? 'Refreshing alerts' : 'Refresh alerts'}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        aria-label="Refresh alerts"
                        disabled={!runId || refresh.isPending || isInitialFindingsLoad}
                        onClick={() => refresh.mutate({ runId })}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={!runId || !openCountByCode.get('BACK_BILLED') || reviewPending}
                    onClick={() => dismissByCode(['BACK_BILLED'], 'Bulk dismissed back bills from File Health.')}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Dismiss Back Bills
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={!runId || !openCountByCode.get('BACK_CREDIT') || reviewPending}
                    onClick={() => dismissByCode(['BACK_CREDIT'], 'Bulk dismissed back credits from File Health.')}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Dismiss Back Credits
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    disabled={!tableRows.some(fileHealthOpenState) || reviewPending}
                    onClick={reviewAll}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Review all
                  </Button>
                </Stack>
              </Stack>
              {totalFindings > visibleFindings ? (
                <Alert severity="info">
                  Showing {visibleFindings.toLocaleString()} of {totalFindings.toLocaleString()} findings. Use a BAC row to drill into full row context.
                </Alert>
              ) : null}
              {tableRows.length ? (
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  <Table size="small" aria-label="File health findings table">
                    <TableHead>
                      <TableRow>
                        <HeadCell id="bac" label="BAC" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="dealer" label="Dealer name" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="severity" label="Severity" align="center" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="memory" label="Memory" align="center" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="status" label="Status" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="source" label="Source" align="center" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <TableCell align="center" sx={{ width: 72 }}>Review</TableCell>
                        <HeadCell id="finding" label="Finding" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                        <HeadCell id="detail" label="Detail" sortBy={sortBy} sortDirection={sortDirection} onSortChange={handleSortChange} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((finding) => (
                        <FindingRow
                          key={finding.id || `${finding.tier}-${finding.fileKind}-${finding.rowNumber}`}
                          finding={finding}
                          onOpenBac={onOpenBac}
                          onReview={reviewFinding}
                          reviewPending={reviewPending}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="success">
                  No file health findings match the current filters.
                </Alert>
              )}
              {pageCount > 1 ? (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {`${(offset + 1).toLocaleString()}-${(offset + visibleFindings).toLocaleString()} of ${totalFindings.toLocaleString()}`}
                  </Typography>
                  <Pagination
                    size="small"
                    count={pageCount}
                    page={page}
                    onChange={(_event, value) => setPage(value)}
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              ) : null}
            </Stack>
          </Collapse>
        )}
      </Stack>
      <ReviewNoteDialog
        open={Boolean(reviewDialogFinding)}
        title="Mark File Health Finding Addressed"
        label="Optional mitigation note"
        initialValue={reviewDialogFinding?.reviewNote || ''}
        confirmLabel="Mark Addressed"
        onClose={() => setReviewDialogFinding(null)}
        onConfirm={submitReview}
      />
      <ReviewNoteDialog
        open={Boolean(bulkReviewRequest)}
        title={bulkReviewRequest?.title || 'Review file health findings'}
        label="Bulk review note"
        initialValue={bulkReviewRequest?.initialNote || ''}
        confirmLabel="Confirm review"
        onClose={() => setBulkReviewRequest(null)}
        onConfirm={confirmBulkReview}
      />
    </Paper>
  );
};

export default FileHealthPanel;
